<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8 pb-12">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>
                </div>

                <div class="sm:flex sm:items-center mt-2">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <!-- Denied state -->
                <zen-denied-state
                    v-if="!canView && !hasPlanFeature('advanced_reporting')"
                    class="mt-4"
                    title="Flag changes over time restricted"
                    message="Flag changes over time needs a higher plan — no sales call, just upgrade in Billing."
                />
                <zen-denied-state
                    v-else-if="!canView"
                    class="mt-4"
                    title="Flag changes over time restricted"
                    message="You don't have permission to view flag change history. Contact your account administrator to request access."
                />

                <template v-if="canView">
                    <div class="mt-2 sm:mt-4 space-y-4">
                        <div class="sm:flex sm:items-center">
                            <div class="sm:flex">
                                <h2 class="text-xl font-medium text-taupe-800">
                                    Flag Changes Over Time
                                </h2>
                            </div>
                        </div>
                        <project-change-notice
                            :project-key="current.projectKey"
                            @refresh="refresh"
                        />

                        <div
                            class="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end sm:gap-4 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                        >
                            <div>
                                <label
                                    for="flag-changes-filter-from"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    From
                                </label>
                                <input
                                    id="flag-changes-filter-from"
                                    type="date"
                                    v-model="filters.startDate"
                                    data-testid="flag-changes-filter-from"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                />
                            </div>
                            <div>
                                <label
                                    for="flag-changes-filter-to"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    To
                                </label>
                                <input
                                    id="flag-changes-filter-to"
                                    type="date"
                                    v-model="filters.endDate"
                                    data-testid="flag-changes-filter-to"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                />
                            </div>
                            <div>
                                <label
                                    for="flag-changes-filter-granularity"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Granularity
                                </label>
                                <select
                                    id="flag-changes-filter-granularity"
                                    v-model="filters.granularity"
                                    data-testid="flag-changes-filter-granularity"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="day">Daily</option>
                                    <option value="week">Weekly</option>
                                    <option value="month">Monthly</option>
                                </select>
                            </div>
                            <div>
                                <label
                                    for="flag-changes-filter-flag"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Flag
                                </label>
                                <select
                                    id="flag-changes-filter-flag"
                                    v-model="filters.flagKey"
                                    data-testid="flag-changes-filter-flag"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="">All flags</option>
                                    <option
                                        v-for="flag in flags"
                                        :key="flag.key"
                                        :value="flag.key"
                                    >
                                        {{ flag.name }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <label
                                    for="flag-changes-filter-actor"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Author
                                </label>
                                <select
                                    id="flag-changes-filter-actor"
                                    v-model="filters.actorUlid"
                                    data-testid="flag-changes-filter-actor"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="">All authors</option>
                                    <option
                                        v-for="actor in actors"
                                        :key="actor.ulid"
                                        :value="actor.ulid"
                                    >
                                        {{ actor.display_name }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <zen-button
                                    variant="secondary"
                                    size="field"
                                    data-testid="flag-changes-apply-filters"
                                    @click="applyFilters"
                                >
                                    Apply
                                </zen-button>
                            </div>
                        </div>

                        <zen-loading-indicator v-if="loading" />

                        <zen-message-block v-if="error" type="error">
                            {{
                                error.message ||
                                'Failed to load flag changes. Please try again.'
                            }}
                        </zen-message-block>

                        <template v-if="!loading">
                            <div
                                v-if="truncated"
                                class="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800"
                                data-testid="flag-changes-truncated-warning"
                            >
                                Showing the most recent {{ events.length }} of
                                {{ totalMatching }} matching changes. Narrow
                                your date range or filters to see the rest.
                            </div>

                            <!-- Chart: evaluation volume with change events overlaid -->
                            <div
                                class="rounded-md border border-taupe-200 bg-white p-4"
                                data-testid="flag-changes-chart"
                            >
                                <LineChart
                                    v-if="chartData.datasets.length > 0"
                                    :data="chartData"
                                    :options="chartOptions"
                                />
                                <zen-empty-table-message
                                    v-else
                                    compact
                                    icon="ZenIconHistory"
                                >
                                    No flag changes or evaluations recorded for
                                    this date range.
                                </zen-empty-table-message>
                            </div>

                            <!-- Grouped summaries -->
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div
                                    class="rounded-md border border-taupe-200 bg-white p-4"
                                    data-testid="flag-changes-by-flag"
                                >
                                    <h2
                                        class="text-sm font-medium text-taupe-700 mb-2"
                                    >
                                        Changes by Flag
                                    </h2>
                                    <zen-empty-table-message
                                        v-if="byFlag.length === 0"
                                        compact
                                        icon="ZenIconHistory"
                                    >
                                        No changes recorded.
                                    </zen-empty-table-message>
                                    <ul
                                        v-else
                                        class="divide-y divide-taupe-100"
                                    >
                                        <li
                                            v-for="row in byFlag"
                                            :key="row.flag_ulid || row.flag_key"
                                            class="flex items-center justify-between py-1.5 text-sm"
                                        >
                                            <span class="text-taupe-800">{{
                                                row.flag_key || 'Unknown flag'
                                            }}</span>
                                            <span
                                                class="font-medium text-taupe-500"
                                                >{{ row.count }}</span
                                            >
                                        </li>
                                    </ul>
                                </div>

                                <div
                                    class="rounded-md border border-taupe-200 bg-white p-4"
                                    data-testid="flag-changes-by-author"
                                >
                                    <h2
                                        class="text-sm font-medium text-taupe-700 mb-2"
                                    >
                                        Changes by Author
                                    </h2>
                                    <zen-empty-table-message
                                        v-if="byAuthor.length === 0"
                                        compact
                                        icon="ZenIconHistory"
                                    >
                                        No changes recorded.
                                    </zen-empty-table-message>
                                    <ul
                                        v-else
                                        class="divide-y divide-taupe-100"
                                    >
                                        <li
                                            v-for="row in byAuthor"
                                            :key="row.actor_ulid || 'system'"
                                            class="flex items-center justify-between py-1.5 text-sm"
                                        >
                                            <span class="text-taupe-800">{{
                                                row.actor_name
                                            }}</span>
                                            <span
                                                class="font-medium text-taupe-500"
                                                >{{ row.count }}</span
                                            >
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <!-- Chronological event list -->
                            <div
                                class="bg-white shadow-sm rounded-lg overflow-hidden"
                                data-testid="flag-changes-event-list"
                            >
                                <zen-empty-table-message
                                    v-if="events.length === 0"
                                    compact
                                    data-testid="flag-changes-empty"
                                    icon="ZenIconHistory"
                                >
                                    No flag changes found matching your filters.
                                </zen-empty-table-message>
                                <ul v-else class="divide-y divide-taupe-200">
                                    <li
                                        v-for="event in events"
                                        :key="event.ulid"
                                        class="hover:bg-taupe-50 cursor-pointer px-4 py-3 sm:px-6"
                                        @click="toggleEventDetail(event.ulid)"
                                        :data-testid="
                                            'flag-changes-event-' + event.ulid
                                        "
                                    >
                                        <div
                                            class="flex items-center justify-between"
                                        >
                                            <div
                                                class="flex items-center space-x-2 min-w-0 flex-wrap gap-y-1"
                                            >
                                                <span
                                                    v-for="changeType in event.change_types"
                                                    :key="changeType"
                                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                                    :class="
                                                        changeTypeBadgeClass(
                                                            changeType
                                                        )
                                                    "
                                                >
                                                    {{
                                                        formatChangeType(
                                                            changeType
                                                        )
                                                    }}
                                                </span>
                                                <span
                                                    class="text-sm font-medium text-taupe-900 truncate"
                                                >
                                                    {{
                                                        event.flag_key ||
                                                        'Unknown flag'
                                                    }}
                                                </span>
                                            </div>
                                            <div
                                                class="ml-2 flex items-center space-x-4 shrink-0"
                                            >
                                                <span
                                                    class="text-sm text-taupe-500"
                                                >
                                                    {{
                                                        event.actor_name ||
                                                        'System'
                                                    }}
                                                </span>
                                                <zen-date-time
                                                    :value="event.created_at"
                                                    human
                                                    class="text-sm text-taupe-400"
                                                />
                                            </div>
                                        </div>

                                        <div
                                            v-if="
                                                expandedEvents.has(event.ulid)
                                            "
                                            class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4"
                                            @click.stop
                                        >
                                            <div v-if="event.before">
                                                <span
                                                    class="text-xs font-medium text-red-600 uppercase tracking-wide"
                                                >
                                                    Before
                                                </span>
                                                <pre
                                                    class="mt-1 bg-red-50 rounded p-3 text-xs text-taupe-700 overflow-x-auto whitespace-pre-wrap break-words"
                                                    >{{
                                                        formatJson(event.before)
                                                    }}</pre>
                                            </div>
                                            <div v-if="event.after">
                                                <span
                                                    class="text-xs font-medium text-green-600 uppercase tracking-wide"
                                                >
                                                    After
                                                </span>
                                                <pre
                                                    class="mt-1 bg-green-50 rounded p-3 text-xs text-taupe-700 overflow-x-auto whitespace-pre-wrap break-words"
                                                    >{{
                                                        formatJson(event.after)
                                                    }}</pre>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </template>

                        <zen-pagination
                            v-if="!loading"
                            :page="pagination.page"
                            :total-pages="pagination.total_pages"
                            :total="pagination.total"
                            @change="changePage"
                        />

                        <div v-if="!loading" class="text-sm pb-10">
                            Report Generated:
                            <zen-date-time :value="generatedAt" />
                        </div>
                    </div>
                </template>
            </div>
        </section>
    </main>
</template>

<script>
import { computed } from 'vue'
import { Line as LineChart } from 'vue-chartjs'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from 'chart.js'

import ReportService from '@/services/report.service'
import FlagService from '@/services/flag.service'
import UserService from '@/services/user.service'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { useSessionStore } from '@/stores/session'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { usePlan } from '@/composables/usePlan'
import { useCancelableLoad } from '@/composables/useCancelableLoad'
import { useServerPagination } from '@/composables/useServerPagination'
import ProjectChangeNotice from '@/screens/_components/project/ProjectChangeNotice.vue'

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
)

// Map (not a plain object) so change-type lookups below don't trip
// eslint-plugin-security's generic object-injection check.
const CHANGE_TYPE_LABELS = new Map([
    ['initial_publish', 'Initial Publish'],
    ['value', 'Value Changed'],
    ['rollout', 'Rollout % Changed'],
    ['rule', 'Targeting Rule Changed'],
    ['republish', 'Republished'],
    ['created', 'Flag Created'],
    ['deleted', 'Flag Deleted'],
    ['metadata', 'Metadata Updated'],
    ['rollback', 'Rollback'],
])

const CHANGE_TYPE_BADGE_CLASSES = new Map([
    ['initial_publish', 'bg-purple-100 text-purple-800'],
    ['value', 'bg-blue-100 text-blue-800'],
    ['rollout', 'bg-indigo-100 text-indigo-800'],
    ['rule', 'bg-teal-100 text-teal-800'],
    ['republish', 'bg-taupe-100 text-taupe-800'],
    ['created', 'bg-green-100 text-green-800'],
    ['deleted', 'bg-red-100 text-red-800'],
    ['metadata', 'bg-blue-100 text-blue-800'],
    ['rollback', 'bg-yellow-100 text-yellow-800'],
])

/**
 * Truncate an ISO timestamp down to the start of its day/week/month bucket,
 * matching the SQL bucketing the API applies to evaluation volume rows, so
 * change-event markers line up with the correct point on the evaluations line.
 */
function truncateToBucket(isoString, granularity) {
    const date = new Date(isoString)

    if (granularity === 'week') {
        const day = (date.getUTCDay() + 6) % 7
        date.setUTCDate(date.getUTCDate() - day)
    } else if (granularity === 'month') {
        date.setUTCDate(1)
    }

    return date.toISOString().slice(0, 10)
}

export default {
    name: 'FlagChangesTimeline',
    components: { LineChart, ProjectChangeNotice },
    setup() {
        const { current } = useCurrentEnvironment()
        const { can } = usePermissions()
        const { hasPlanFeature } = usePlan()
        const { loading, error, run } = useCancelableLoad()
        const { pagination, setPage, resetPage, applyResponse, resetOnError } =
            useServerPagination({ perPage: 50 })
        return {
            current,
            sessionStore: useSessionStore(),
            hasPlanFeature,
            canView: computed(
                () =>
                    can(PERMISSIONS.AUDIT_LOG_READ) &&
                    hasPlanFeature('advanced_reporting')
            ),
            loading,
            error,
            run,
            pagination,
            setPage,
            resetPage,
            applyResponse,
            resetOnError,
        }
    },
    data() {
        return {
            truncated: false,
            totalMatching: 0,
            generatedAt: null,
            events: [],
            byFlag: [],
            byAuthor: [],
            evaluationSeries: [],
            flags: [],
            actors: [],
            expandedEvents: new Set(),
            filters: {
                startDate: '',
                endDate: '',
                granularity: 'day',
                flagKey: '',
                actorUlid: '',
            },
        }
    },
    computed: {
        environmentScopeKey() {
            return `${this.current.projectKey}:${this.current.environmentKey}`
        },
        bucketLabels() {
            const fromSeries = this.evaluationSeries.map((s) => s.bucket)
            const fromEvents = this.events.map((event) =>
                truncateToBucket(event.created_at, this.filters.granularity)
            )
            return Array.from(new Set([...fromSeries, ...fromEvents])).sort()
        },
        totalsByBucket() {
            const map = new Map()
            this.evaluationSeries.forEach((row) => {
                map.set(row.bucket, row.total)
            })
            return map
        },
        chartData() {
            const labels = this.bucketLabels
            const totals = labels.map(
                (bucket) => this.totalsByBucket.get(bucket) ?? 0
            )

            const eventPoints = this.events.map((event) => {
                const bucket = truncateToBucket(
                    event.created_at,
                    this.filters.granularity
                )
                return {
                    x: bucket,
                    y: this.totalsByBucket.get(bucket) ?? 0,
                    event,
                }
            })

            const datasets = []

            if (totals.length > 0) {
                datasets.push({
                    type: 'line',
                    label: 'Evaluations',
                    data: totals,
                    borderColor: '#0ea5e9',
                    backgroundColor: '#0ea5e9',
                    tension: 0.2,
                    pointRadius: 0,
                })
            }

            if (eventPoints.length > 0) {
                datasets.push({
                    type: 'line',
                    label: 'Flag changes',
                    data: eventPoints,
                    showLine: false,
                    pointStyle: 'triangle',
                    pointRadius: 7,
                    pointHoverRadius: 9,
                    backgroundColor: '#f97316',
                    borderColor: '#f97316',
                })
            }

            return { labels, datasets }
        },
        chartOptions() {
            return {
                responsive: true,
                interaction: { mode: 'nearest', intersect: true },
                scales: {
                    y: { beginAtZero: true },
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const event = context.raw?.event
                                if (!event) {
                                    return `Evaluations: ${context.formattedValue}`
                                }
                                const changeTypes = event.change_types
                                    .map((t) => this.formatChangeType(t))
                                    .join(', ')
                                return `${event.flag_key || 'Unknown flag'}: ${changeTypes} (${event.actor_name || 'System'})`
                            },
                        },
                    },
                },
            }
        },
    },
    watch: {
        environmentScopeKey() {
            this.refresh()
        },
    },
    methods: {
        applyFilters() {
            this.resetPage()
            this.load()
        },
        changePage(page) {
            this.setPage(page)
            this.load()
        },
        formatChangeType(changeType) {
            return CHANGE_TYPE_LABELS.get(changeType) || changeType
        },
        changeTypeBadgeClass(changeType) {
            return (
                CHANGE_TYPE_BADGE_CLASSES.get(changeType) ||
                'bg-taupe-100 text-taupe-800'
            )
        },
        formatJson(obj) {
            if (!obj) return ''
            return JSON.stringify(obj, null, 2)
        },
        toggleEventDetail(ulid) {
            const expanded = new Set(this.expandedEvents)
            if (expanded.has(ulid)) {
                expanded.delete(ulid)
            } else {
                expanded.add(ulid)
            }
            this.expandedEvents = expanded
        },
        defaultDateRange() {
            const end = new Date()
            const start = new Date()
            start.setDate(start.getDate() - 29)

            return {
                startDate: start.toISOString().slice(0, 10),
                endDate: end.toISOString().slice(0, 10),
            }
        },
        load() {
            if (!this.canView) return

            this.expandedEvents = new Set()

            this.run(
                (signal) =>
                    ReportService.getFlagChangeTimeline(
                        this.current.projectKey,
                        this.current.environmentKey,
                        {
                            startDate: this.filters.startDate,
                            endDate: this.filters.endDate,
                            granularity: this.filters.granularity,
                            flagKey: this.filters.flagKey || undefined,
                            actorUlid: this.filters.actorUlid || undefined,
                            page: this.pagination.page,
                            perPage: this.pagination.per_page,
                        },
                        { signal }
                    ),
                (response) => {
                    this.events = response.data.events
                    this.byFlag = response.data.by_flag
                    this.byAuthor = response.data.by_author
                    this.evaluationSeries = response.data.evaluation_time_series
                    this.truncated = response.data.truncated
                    this.totalMatching = response.data.total_matching
                    this.generatedAt = response.data.generated_at
                    if (this.applyResponse(response.data.pagination)) {
                        this.load()
                    }
                },
                () => {
                    this.events = []
                    this.byFlag = []
                    this.byAuthor = []
                    this.evaluationSeries = []
                    this.resetOnError()
                }
            )
        },
        refresh() {
            this.applyFilters()

            if (this.canView) {
                FlagService.list(this.current.projectKey)
                    .then((response) => {
                        this.flags = response.data ?? []
                    })
                    .catch(() => {
                        this.flags = []
                    })

                UserService.list()
                    .then((response) => {
                        this.actors = response.data ?? []
                    })
                    .catch(() => {
                        this.actors = []
                    })
            }
        },
    },
    mounted() {
        Object.assign(this.filters, this.defaultDateRange())
        this.refresh()
    },
}
</script>
